import { AlignLeft, AlignCenter, AlignRight, AlignJustify } from 'lucide-react';
import type { TextElement } from '@fotoboek/domain';
import { Label, Input, Textarea } from '../../ui/Input';
import { t } from '../../../i18n';
import './text-properties.css';

const FONTS = ['Source Sans 3', 'Source Serif 4'];
const ALIGNMENTS: Array<{ value: TextElement['align']; icon: typeof AlignLeft }> = [
  { value: 'left', icon: AlignLeft },
  { value: 'center', icon: AlignCenter },
  { value: 'right', icon: AlignRight },
  { value: 'justify', icon: AlignJustify },
];

export function TextProperties({
  element,
  onChange,
}: {
  element: TextElement;
  onChange: (patch: Partial<TextElement>) => void;
}) {
  return (
    <div className="properties-section">
      <div className="left-panel-section-title">{t('editor.properties.text.heading')}</div>
      <div className="field-group">
        <Label>{t('editor.properties.text.content')}</Label>
        <Textarea value={element.content} onChange={(e) => onChange({ content: e.target.value })} />
      </div>
      <div className="field-group">
        <Label>{t('editor.properties.text.font')}</Label>
        <select
          className="text-font-select"
          value={element.fontFamily}
          onChange={(e) => onChange({ fontFamily: e.target.value })}
        >
          {FONTS.map((font) => (
            <option key={font} value={font}>
              {font}
            </option>
          ))}
        </select>
      </div>
      <div className="properties-row">
        <div className="field-group">
          <Label>{t('editor.properties.text.size')}</Label>
          <Input
            type="number"
            min={6}
            value={element.fontSizePt}
            onChange={(e) => onChange({ fontSizePt: Math.max(6, Number(e.target.value)) })}
          />
        </div>
        <div className="field-group">
          <Label>{t('editor.properties.text.weight')}</Label>
          <select
            className="text-font-select"
            value={element.fontWeight}
            onChange={(e) =>
              onChange({ fontWeight: Number(e.target.value) as TextElement['fontWeight'] })
            }
          >
            {[400, 500, 600, 700].map((w) => (
              <option key={w} value={w}>
                {w}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="field-group">
        <Label>{t('editor.properties.text.color')}</Label>
        <input
          type="color"
          value={element.color}
          onChange={(e) => onChange({ color: e.target.value })}
        />
      </div>
      <div className="field-group">
        <Label>{t('editor.properties.text.align')}</Label>
        <div className="text-align-buttons">
          {ALIGNMENTS.map(({ value, icon: Icon }) => (
            <button
              key={value}
              className={element.align === value ? 'text-align-btn active' : 'text-align-btn'}
              onClick={() => onChange({ align: value })}
            >
              <Icon size={14} />
            </button>
          ))}
        </div>
      </div>
      <div className="properties-row">
        <div className="field-group">
          <Label>{t('editor.properties.text.lineHeight')}</Label>
          <Input
            type="number"
            step={0.1}
            min={0.8}
            value={element.lineHeight}
            onChange={(e) => onChange({ lineHeight: Number(e.target.value) })}
          />
        </div>
        <div className="field-group">
          <Label>{t('editor.properties.text.letterSpacing')}</Label>
          <Input
            type="number"
            step={0.01}
            value={element.letterSpacingEm}
            onChange={(e) => onChange({ letterSpacingEm: Number(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
}
